import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDropdown from '../../components/GenericDropDown'; // Import GenericDropdown
import GenericInput from '../../components/GenericInput';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { GameConfig } from '../../models/GameConfig';
import { getAllGames } from '../../redux/entities/gameEntity';
import { getAllResultTimes } from '../../redux/entities/resultTimeEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';


const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 2rem;

  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: flex-start;
  }
`;

interface AddGameConfigPageProps {
  onSave: (value: GameConfig) => void;  // Save callback function
  data: GameConfig;  // Game data for editing
}

const AddGameConfigPage: React.FC<AddGameConfigPageProps> = ({ data, onSave }) => {

  const [gameConfig, setGameConfig] = useState<GameConfig>(data);
  const [selectedGame, setSelectedGame] = useState<string>(gameConfig?.game?.id?.toString());  // Keep this as string for the dropdown value
  const [selectedResultTime, setSelectedResultTime] = useState<string>(gameConfig?.resultTime?.id?.toString());
  const [selectedLimitInterval, setSelectedLimitInterval] = useState<string>(gameConfig?.limitTimeInterval?.toString());

  const [hasLoaded, setHasLoaded] = useState(false);

  const dispatch = useAppDispatch();
  let games = useAppSelector((state) => state.entities.gameReducer.games);
  let resultTimes = useAppSelector((state) => state.entities.resultTimeReducer.resultTimes);
  const { showModalStrip } = useModalStrip();

  useEffect(() => {
    // Dispatch getAllGames action only if games are not already loaded
    if (((games && games.length === 0) || (resultTimes && resultTimes.length === 0)) && !hasLoaded) {
      dispatch(getAllGames())
      dispatch(getAllResultTimes())
        .unwrap()
        .then(() => setHasLoaded(true))
        .catch((err: Error) => {
          showModalStrip('danger', err?.message, 5000);
        });
    }
  }, [dispatch, games, hasLoaded, resultTimes, showModalStrip]);

  const gameOptions = games.filter(x => x.status === 'ACTIVE').map((game) => ({
    value: game.id.toString(),  // Use game ID as a string for dropdown
    label: game.name,  // Display game name
  }));

  const resultTimeOptions = resultTimes.map((resultTime) => ({
    value: resultTime.id.toString(),  // Use game ID as a string for dropdown
    label: resultTime.resultTime,  // Display game name
  }));

  const limitTimeOptions = ["5", "10", "15", "20", "25", "30"].map((interval) => ({
    value: interval.toString(),  // Use game ID as a string for dropdown
    label: interval,  // Display game name
  }));

  const handleGameChange = (selectedGameId: string) => {
    // Find the full game object using the selected game ID
    const selectedGameObject = games.find(game => game.id.toString() === selectedGameId);
    if (selectedGameObject) {
      setSelectedGame(selectedGameId);  // Update the selected game ID (string)
      setGameConfig((prevConfig) => ({
        ...prevConfig,
        game: selectedGameObject,  // Set the whole game object
      }));
    }
  };

  const handleResultTimeChange = (selectedGameId: string) => {
    // Find the full game object using the selected game ID
    const selectedGameObject = resultTimes.find(game => game.id.toString() === selectedGameId);
    if (selectedGameObject) {
      setSelectedResultTime(selectedGameId);  // Update the selected game ID (string)
      setGameConfig((prevConfig) => ({
        ...prevConfig,
        resultTime: selectedGameObject,  // Set the whole game object
      }));
    }
  };


  const handleAddGameDetails = () => {
    console.log(gameConfig);
    if (selectedGame && selectedResultTime) {

      setSelectedGame('');
      setSelectedResultTime('');

      onSave(gameConfig);

    } else {
      alert('Please fill all fields.');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const parsedValue = value === "" ? null : parseInt(value); // If value is empty, set it to null
    setGameConfig((prevConfig) => ({
      ...prevConfig,
      [field]: parsedValue,
    }));
  };

  return (
    <Container>
      {/* Add New User Button and Search Box */}
      <SearchWrapper>
        {/* Game Dropdown - Dynamically populated from API */}
        <GenericDropdown
          label="Game"
          options={gameOptions}
          value={selectedGame}  // Pass selected game ID (string)
          onChange={handleGameChange} // Pass the selected game ID to the handler
          disabled={gameConfig.id != null}
        />

        {/* Result Time Dropdown */}
        <GenericDropdown
          label="Result Time"
          options={resultTimeOptions}
          value={selectedResultTime}
          onChange={handleResultTimeChange}
        />

        {/* Limit Time Dropdown */}
        <GenericDropdown
          label="Limit Time Interval"
          options={limitTimeOptions}
          value={selectedLimitInterval}
          onChange={(value) => {
            setSelectedLimitInterval(value);
            setGameConfig({
              ...gameConfig,
              limitTimeInterval: parseInt(value)
            });
          }}
        />

        {gameConfig?.game?.singlePattiSupported && (
          <div>
            <GenericInput
              label="Single Min"
              value={gameConfig.singleMin?.toString() || ''}
              onChange={(value) => handleInputChange('singleMin', value)}
            />
            <GenericInput
              label="Single Max"
              value={gameConfig.singleMax?.toString() || ''}
              onChange={(value) => handleInputChange('singleMax', value)}
            />
            <GenericInput
              label="Single Limit Min"
              value={gameConfig.singleLimitMin?.toString() || ''}
              onChange={(value) => handleInputChange('singleLimitMin', value)}
            />
            <GenericInput
              label="Single Limit Max"
              value={gameConfig.singleLimitMax?.toString() || ''}
              onChange={(value) => handleInputChange('singleLimitMax', value)}
            />
            <GenericInput
              label="Panna Min"
              value={gameConfig.pattiMin?.toString() || ''}
              onChange={(value) => handleInputChange('pattiMin', value)}
            />
            <GenericInput
              label="Panna Max"
              value={gameConfig.pattiMax?.toString() || ''}
              onChange={(value) => handleInputChange('pattiMax', value)}
            />
            <GenericInput
              label="Panna Limit Min"
              value={gameConfig.pattiLimitMin?.toString() || ''}
              onChange={(value) => handleInputChange('pattiLimitMin', value)}
            />
            <GenericInput
              label="Panna Limit Max"
              value={gameConfig.pattiLimitMax?.toString() || ''}
              onChange={(value) => handleInputChange('pattiLimitMax', value)}
            />
            <GenericInput
              label="Win Multiplier Single"
              value={gameConfig.winMultiplierSingle?.toString() || ''}
              onChange={(value) => handleInputChange('winMultiplierSingle', value)}
            />
            <GenericInput
              label="Win Multiplier Panna"
              value={gameConfig.winMultiplierPatti?.toString() || ''}
              onChange={(value) => handleInputChange('winMultiplierPatti', value)}
            />
          </div>
        )}

        {gameConfig?.game?.digitSupported &&
          <div>
            <GenericInput
              label="Digit1 Min"
              value={gameConfig.digit1Min?.toString() || ''}
              onChange={(value) => handleInputChange('digit1Min', value)}
            />
            <GenericInput
              label="Digit1 Max"
              value={gameConfig.digit1Max?.toString() || ''}
              onChange={(value) => handleInputChange('digit1Max', value)}
            />
            <GenericInput
              label="Digit2 Min"
              value={gameConfig.digit2Min?.toString() || ''}
              onChange={(value) => handleInputChange('digit2Min', value)}
            />
            <GenericInput
              label="Digit2 Max"
              value={gameConfig.digit2Max?.toString() || ''}
              onChange={(value) => handleInputChange('digit2Max', value)}
            />
            <GenericInput
              label="Digit3 Min"
              value={gameConfig.digit3Min?.toString() || ''}
              onChange={(value) => handleInputChange('digit3Min', value)}
            />
            <GenericInput
              label="Digit3 Max"
              value={gameConfig.digit3Max?.toString() || ''}
              onChange={(value) => handleInputChange('digit3Max', value)}
            />
            <GenericInput
              label="Digit4 Min"
              value={gameConfig.digit4Min?.toString() || ''}
              onChange={(value) => handleInputChange('digit4Min', value)}
            />
            <GenericInput
              label="Digit4 Max"
              value={gameConfig.digit4Max?.toString() || ''}
              onChange={(value) => handleInputChange('digit4Max', value)}
            />
            <GenericInput
              label="Digit5 Min"
              value={gameConfig.digit5Min?.toString() || ''}
              onChange={(value) => handleInputChange('digit5Min', value)}
            />
            <GenericInput
              label="Digit5 Max"
              value={gameConfig.digit5Max?.toString() || ''}
              onChange={(value) => handleInputChange('digit5Max', value)}
            />
            <GenericInput
              label="Digit6 Min"
              value={gameConfig.digit6Min?.toString() || ''}
              onChange={(value) => handleInputChange('digit6Min', value)}
            />
            <GenericInput
              label="Digit6 Max"
              value={gameConfig.digit6Max?.toString() || ''}
              onChange={(value) => handleInputChange('digit6Max', value)}
            />
          </div>
        }

        {gameConfig?.game?.burfiSupported && (
          <div>
            <GenericInput
              label="Barfi Min"
              value={gameConfig.burfiMin?.toString() || ''}
              onChange={(value) => handleInputChange('burfiMin', value)}
            />
            <GenericInput
              label="Burfi Max"
              value={gameConfig.burfiMax?.toString() || ''}
              onChange={(value) => handleInputChange('burfiMax', value)}
            />
            <GenericInput
              label="Barfi Limit Min"
              value={gameConfig.burfiLimitMin?.toString() || ''}
              onChange={(value) => handleInputChange('burfiLimitMin', value)}
            />
            <GenericInput
              label="Barfi Limit Max"
              value={gameConfig.burfiLimitMax?.toString() || ''}
              onChange={(value) => handleInputChange('burfiLimitMax', value)}
            />
            <GenericInput
              label="First Digit Win Multiplier"
              value={gameConfig.burfiFirstDigitWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiFirstDigitWinMultiplier', value)}
            />
            <GenericInput
              label="Last Digit Win Multiplier"
              value={gameConfig.burfiLastDigitWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiLastDigitWinMultiplier', value)}
            />
            <GenericInput
              label="Even Pair Win Multiplier"
              value={gameConfig.burfiEvenPairWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiEvenPairWinMultiplier', value)}
            />
            <GenericInput
              label="Odd Pair Win Multiplier"
              value={gameConfig.burfiOddPairWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiOddPairWinMultiplier', value)}
            />
            <GenericInput
              label="Mismatch Win Multiplier"
              value={gameConfig.burfiMatchWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiMatchWinMultiplier', value)}
            />
            <GenericInput
              label="Match Win Multiplier"
              value={gameConfig.burfiMismatchWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiMismatchWinMultiplier', value)}
            />
            <GenericInput
              label="Result Digit Win Multiplier"
              value={gameConfig.burfiResultWinMultiplier?.toString() || ''}
              onChange={(value) => handleInputChange('burfiResultWinMultiplier', value)}
            />
            <GenericInput
              label="Barfi Single Commission Rate"
              value={gameConfig.burfiSingleCommissionRate?.toString() || ''}
              onChange={(value) => handleInputChange('burfiSingleCommissionRate', value)}
            />
            <GenericInput
              label="Barfi Pair Commission Rate"
              value={gameConfig.burfiPairCommissionRate?.toString() || ''}
              onChange={(value) => handleInputChange('burfiPairCommissionRate', value)}
            />
            <GenericInput
              label="Barfi Match Commission Rate"
              value={gameConfig.burfiMatchCommissionRate?.toString() || ''}
              onChange={(value) => handleInputChange('burfiMatchCommissionRate', value)}
            />
          </div>
        )}

        <GenericButton
          label={gameConfig.id ? 'Update Game Config' : 'Add Game Config'}
          onClick={() => handleAddGameDetails()}
        />
      </SearchWrapper>
    </Container>
  );
};

export default AddGameConfigPage;
